// frontend/src/components/cart/ConfirmOrder.js - Enhanced with delivery area editing

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { clearCart, saveShippingInfo } from '../../slices/cartSlice';
import {
    Truck,
    MapPin,
    Phone,
    User,
    IndianRupee,
    Loader2,
    ShoppingBag,
    Edit2,
    RefreshCw,
    CheckCircle,
    AlertTriangle,
    X,
    ArrowLeft
} from 'lucide-react';
import MetaData from '../layouts/MetaData';
import CheckoutSteps from './CheckoutStep';
import { OrderItemWeight } from '../cart/WeightDisplay';

const ConfirmOrder = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [editingDelivery, setEditingDelivery] = useState(false);
    const [pincodeLoading, setPincodeLoading] = useState(false);
    const [newDeliveryInfo, setNewDeliveryInfo] = useState(null);
    const [selectedPincode, setSelectedPincode] = useState('');
    const [manualPincode, setManualPincode] = useState('');
    const [showManualInput, setShowManualInput] = useState(false);
    const [pincodeError, setPincodeError] = useState('');

    const { shippingInfo, items: cartItems = [] } = useSelector(state => state.cartState);
    const { user, isAuthenticated } = useSelector(state => state.authState);
    const [paymentMethod, setPaymentMethod] = useState('');
    const [guestInfo, setGuestInfo] = useState(null);

    // Available delivery areas
    const deliveryAreas = [
        { code: '560098', area: 'Rajarajeshwari Nagar', hubPlace: 'Kengeri' },
        { code: '560060', area: 'Kengeri', hubPlace: 'Kengeri' },
        { code: '560060', area: 'Viswaneedam', hubPlace: 'Segehalli' },
        { code: '560056', area: 'Bangalore Viswavidalaya', hubPlace: 'Kengeri' },
        { code: '562130', area: 'Seegehalli', hubPlace: 'Segehalli' }
    ];

    // Calculate item prices without discounts
    const calculateItemPrice = (item) => {
        const basePrice = item.priceType === 'perDozen' ?
                         (item.dozenPrice || item.price) :
                         item.priceType === 'perPack' ?
                         (item.packPrice || item.price) :
                         item.price;
        
        return basePrice * item.quantity;
    };

    const itemsPrice = cartItems.reduce(
        (acc, item) => acc + calculateItemPrice(item),
        0
    );

    // Get current delivery charge (50 by default, but can be updated)
    const getCurrentDeliveryCharge = () => {
        if (newDeliveryInfo) {
            return newDeliveryInfo.deliveryCharge;
        }
        // Try to get from localStorage (saved from cart page)
        const savedDeliveryInfo = localStorage.getItem('selectedDeliveryInfo');
        if (savedDeliveryInfo) {
            try {
                const parsed = JSON.parse(savedDeliveryInfo);
                return parsed.deliveryCharge;
            } catch (error) {
                console.error('Error parsing saved delivery info:', error);
            }
        }
        return 50; // Default delivery charge
    };

    const shippingPrice = getCurrentDeliveryCharge();
    const totalPrice = itemsPrice + shippingPrice;

    useEffect(() => {
        if (!shippingInfo || !cartItems.length) {
            navigate('/cart');
            return;
        }
        if (!isAuthenticated) {
            const isGuestCheckout = localStorage.getItem('isGuestCheckout');
            const storedGuestInfo = localStorage.getItem('guestInfo');
            
            if (!isGuestCheckout || !storedGuestInfo) {
                navigate('/cart');
                return;
            }
            
            setGuestInfo(JSON.parse(storedGuestInfo));
        }

        // Set current pincode for editing
        setSelectedPincode(shippingInfo.postalCode || '');
    }, [navigate, shippingInfo, cartItems, isAuthenticated]);

    // Check pincode delivery charges
    const checkPincode = async (pincode) => {
        if (!pincode || pincode.length !== 6) {
            setPincodeError('Please enter a valid 6-digit pincode');
            return false;
        }

        try {
            setPincodeLoading(true);
            setPincodeError('');

            const { data } = await axios.post('/api/v1/pincode/check', {
                pincode: pincode
            });

            if (data.success && data.isServiceable) {
                setNewDeliveryInfo(data.data);
                setSelectedPincode(pincode);

                // Save to localStorage for persistence
                localStorage.setItem('selectedDeliveryInfo', JSON.stringify(data.data));
                localStorage.setItem('selectedPincode', pincode);

                toast.success(data.message);
                return true;
            } else {
                setPincodeError(data.message);
                setNewDeliveryInfo(null);
                return false;
            }
        } catch (error) {
            console.error('Pincode check error:', error);
            const errorMessage = error.response?.data?.message || 'Error checking pincode';
            setPincodeError(errorMessage);
            setNewDeliveryInfo(null);
            return false;
        } finally {
            setPincodeLoading(false);
        }
    };

    // Handle dropdown selection
    const handleAreaSelect = async (selectedPincode) => {
        if (selectedPincode) {
            const success = await checkPincode(selectedPincode);
            if (success) {
                setShowManualInput(false);
            }
        } else {
            setNewDeliveryInfo(null);
            setSelectedPincode('');
            setPincodeError('');
        }
    };

    // Handle manual pincode input
    const handleManualPincodeCheck = async () => {
        const success = await checkPincode(manualPincode);
        if (success) {
            setManualPincode('');
            setShowManualInput(false);
        }
    };

    // Save delivery area changes
    const saveDeliveryChanges = () => {
        if (!newDeliveryInfo || !selectedPincode) {
            toast.error('Please select a valid delivery area');
            return;
        }

        // Update shipping info with new delivery details
        const updatedShippingInfo = {
            ...shippingInfo,
            postalCode: selectedPincode,
            areaName: newDeliveryInfo.area,
            hubPlace: newDeliveryInfo.hubPlace
        };

        dispatch(saveShippingInfo(updatedShippingInfo));
        
        toast.success('Delivery area updated successfully');
        setEditingDelivery(false);
        setPincodeError('');
    };

    // Cancel delivery area editing
    const cancelDeliveryEdit = () => {
        setEditingDelivery(false);
        setNewDeliveryInfo(null);
        setSelectedPincode(shippingInfo.postalCode || '');
        setManualPincode('');
        setShowManualInput(false);
        setPincodeError('');
    };

    const processToPayment = async () => {
        if (!paymentMethod) {
            toast.error('Please select a payment method');
            return;
        }

        try {
            setLoading(true);

            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    'x-guest-checkout': !isAuthenticated ? 'true' : 'false'
                }
            };

            const processedOrderItems = cartItems.map(item => ({
                product: item.product,
                name: item.name,
                price: item.price,
                image: item.image,
                quantity: item.quantity,
                priceType: item.priceType,
                selectedWeight: item.selectedWeight,
                dozenPrice: item.dozenPrice,
                packPrice: item.packPrice
            }));

            if (paymentMethod === 'COD') {
                const orderData = {
                    orderItems: processedOrderItems,
                    shippingInfo: {
                        ...shippingInfo,
                        phoneno: shippingInfo.phoneNo
                    },
                    itemsPrice,
                    shippingPrice,
                    totalPrice,
                    paymentInfo: {
                        id: 'COD_PAYMENT',
                        status: 'Processing',
                        paymentMethod: 'COD'
                    },
                    guestInfo: !isAuthenticated ? guestInfo : undefined
                };

                const { data } = await axios.post('/api/v1/order/new', orderData, config);

                if (data.success) {
                    navigate('/success', {
                        replace: true,
                        state: {
                            orderInfo: {
                                ...orderData,
                                orderId: data.order.orderId,
                                createdAt: new Date().toISOString()
                            }
                        }
                    });

                    setTimeout(() => {
                        dispatch(clearCart());
                        localStorage.removeItem('cartItems');
                        sessionStorage.removeItem('orderInfo');
                        
                        if (!isAuthenticated) {
                            localStorage.removeItem('guestInfo');
                            localStorage.removeItem('isGuestCheckout');
                        }
                    }, 100);
                }
            } else {
                sessionStorage.setItem('orderInfo', JSON.stringify({
                    itemsPrice,
                    shippingPrice,
                    totalPrice,
                    shippingInfo,
                    orderItems: processedOrderItems,
                    guestInfo: !isAuthenticated ? guestInfo : undefined
                }));

                navigate('/payment');
            }
        } catch (error) {
            console.error('Order processing error:', error);
            toast.error(error.response?.data?.message || 'Error processing order');
        } finally {
            setLoading(false);
        }
    };

    // Get current delivery info for display
    const getCurrentDeliveryInfo = () => {
        if (newDeliveryInfo) return newDeliveryInfo;
        
        // Try to get from localStorage
        const savedDeliveryInfo = localStorage.getItem('selectedDeliveryInfo');
        if (savedDeliveryInfo) {
            try {
                return JSON.parse(savedDeliveryInfo);
            } catch (error) {
                console.error('Error parsing saved delivery info:', error);
            }
        }
        
        // Fallback to shipping info
        return {
            area: shippingInfo?.areaName || 'Unknown Area',
            hubPlace: shippingInfo?.hubPlace || 'Unknown Hub',
            deliveryCharge: 50
        };
    };

    const currentDeliveryInfo = getCurrentDeliveryInfo();

    return (
        <>
            <MetaData title={'Confirm Order'} />
            <div className="min-h-screen bg-gray-50 py-3 sm:py-6" style={{
                backgroundImage: `url(https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-image-web-banner-background-5.png)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
            }}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <CheckoutSteps shipping confirmOrder />
                    
                    <div className="mt-6 flex justify-end">
                        <button
                            type="button"
                            onClick={() => {
                                if (isAuthenticated) {
                                    navigate(-1);
                                } else {
                                    // Guest: restore guest-checkout to step 2 so their data is not lost
                                    const guestInfoStored = localStorage.getItem('guestInfo');
                                    const guestInfoObj = guestInfoStored ? JSON.parse(guestInfoStored) : guestInfo;
                                    const guestFormData = {
                                        name: guestInfoObj?.name || '',
                                        email: guestInfoObj?.email || '',
                                        phone: guestInfoObj?.phone || shippingInfo?.phoneNo || '',
                                        address: shippingInfo?.address || '',
                                        postalCode: shippingInfo?.postalCode || '',
                                        city: shippingInfo?.city || 'Bengaluru',
                                        state: shippingInfo?.state || 'Karnataka',
                                        country: shippingInfo?.country || 'India'
                                    };
                                    localStorage.setItem('guestFormData', JSON.stringify(guestFormData));
                                    localStorage.setItem('guestCheckoutStep', '2');
                                    if (shippingInfo?.postalCode) {
                                        const deliveryInfo = {
                                            area: shippingInfo?.areaName,
                                            hubPlace: shippingInfo?.hubPlace,
                                            deliveryCharge: 50
                                        };
                                        localStorage.setItem('selectedDeliveryInfo', JSON.stringify(deliveryInfo));
                                        localStorage.setItem('selectedPincode', shippingInfo.postalCode);
                                    }
                                    navigate('/guest-checkout');
                                }
                            }}
                            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-[#dc2626] hover:bg-white/80 rounded-lg transition-colors font-medium"
                        >
                            <ArrowLeft className="w-5 h-5" />
                            Go Back
                        </button>
                    </div>
                    
                    <div className="mt-4">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            {/* Left Column - Order Details */}
                            <div className="lg:col-span-2 space-y-6">
                                {/* Shipping Info with Edit Option */}
                                <div className="rounded-xl shadow-sm p-6 bg-white">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <Truck className="w-6 h-6 sm:h-6 sm:w-6 text-[#dc2626]" />
                                            <h2 className="text-xl font-bold text-gray-900 font-['Crimson_Text']">
                                                Shipping Information
                                            </h2>
                                        </div>
                                        {!editingDelivery && (
                                            <button
                                                onClick={() => setEditingDelivery(true)}
                                                className="flex items-center gap-2 text-[#dc2626] hover:text-[#dc2626]/80 transition-colors text-sm"
                                            >
                                                <Edit2 className="w-4 h-4" />
                                                Edit Delivery Area
                                            </button>
                                        )}
                                    </div>
                                    
                                    <div className="grid gap-4">
                                        <div className="flex items-center gap-3">
                                            <User className="w-5 h-5 text-gray-400" />
                                            <div>
                                                <p className="text-sm text-gray-500">Name</p>
                                                <p className="font-medium">{isAuthenticated ? user?.name : guestInfo?.name}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Phone className="w-5 h-5 text-gray-400" />
                                            <div>
                                                <p className="text-sm text-gray-500">Phone</p>
                                                <p className="font-medium">{shippingInfo?.phoneNo}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                                            <div>
                                                <p className="text-sm text-gray-500">Delivery Address</p>
                                                <p className="font-medium">
                                                    {shippingInfo?.address}, {shippingInfo?.city} - {shippingInfo?.postalCode},
                                                    {shippingInfo?.state}, {shippingInfo?.country}
                                                </p>
                                                <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                                                    <p className="text-sm font-medium text-green-800">
                                                        📍 Delivery Area: {currentDeliveryInfo.area}
                                                    </p>
                                                    <p className="text-xs text-green-600 mt-1">
                                                        Hub: {currentDeliveryInfo.hubPlace} | Delivery Charge: ₹{currentDeliveryInfo.deliveryCharge}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Delivery Area Editing Section */}
                                    {editingDelivery && (
                                        <div className="mt-6 p-4 border-2 border-[#dc2626] rounded-lg bg-red-50">
                                            <div className="flex items-center justify-between mb-4">
                                                <h3 className="text-lg font-semibold text-[#dc2626]">
                                                    Edit Delivery Area
                                                </h3>
                                                <button
                                                    onClick={cancelDeliveryEdit}
                                                    className="text-gray-500 hover:text-gray-700"
                                                >
                                                    <X className="w-5 h-5" />
                                                </button>
                                            </div>

                                            {/* Dropdown Selection */}
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Select Delivery Area
                                                    </label>
                                                    <select
                                                        value={selectedPincode}
                                                        onChange={(e) => handleAreaSelect(e.target.value)}
                                                        className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] text-sm"
                                                    >
                                                        <option value="">Select delivery area</option>
                                                        {deliveryAreas.map((area, index) => (
                                                            <option key={`${area.code}-${index}`} value={area.code}>
                                                                {area.area} - {area.code} (Hub: {area.hubPlace})
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                {/* Manual Pincode Input Option */}
                                                <div className="text-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowManualInput(!showManualInput)}
                                                        className="text-sm text-[#dc2626] hover:text-[#dc2626]/80 flex items-center justify-center gap-1 mx-auto"
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                        {showManualInput ? 'Use dropdown instead' : 'Or enter pincode manually'}
                                                    </button>
                                                </div>

                                                {/* Manual Pincode Input */}
                                                {showManualInput && (
                                                    <div className="border border-gray-200 rounded-lg p-3 bg-white">
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Enter 6-digit Pincode
                                                        </label>
                                                        <div className="flex gap-2">
                                                            <input
                                                                type="text"
                                                                maxLength="6"
                                                                value={manualPincode}
                                                                onChange={(e) => {
                                                                    const value = e.target.value.replace(/\D/g, '');
                                                                    setManualPincode(value);
                                                                    setPincodeError('');
                                                                }}
                                                                className="flex-grow px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626]"
                                                                placeholder="Enter pincode"
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={handleManualPincodeCheck}
                                                                disabled={pincodeLoading || manualPincode.length !== 6}
                                                                className="px-4 py-2 bg-[#dc2626] text-white text-sm rounded-lg hover:bg-[#dc2626]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                                            >
                                                                {pincodeLoading ? (
                                                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                                                ) : (
                                                                    'Check'
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Error Display */}
                                                {pincodeError && (
                                                    <div className="flex items-start gap-2 text-red-600 text-sm">
                                                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                                        <p>{pincodeError}</p>
                                                    </div>
                                                )}

                                                {/* New Delivery Info Preview */}
                                                {newDeliveryInfo && (
                                                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                                        <div className="flex items-start gap-2">
                                                            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                                                            <div>
                                                                <p className="font-medium text-sm text-green-800">
                                                                    ✅ New Area: {newDeliveryInfo.area}
                                                                </p>
                                                                <p className="text-xs text-green-600 mt-1">
                                                                    📍 Hub: {newDeliveryInfo.hubPlace} | 🚚 Delivery Charge: ₹{newDeliveryInfo.deliveryCharge}
                                                                </p>
                                                                <p className="text-xs text-green-600">
                                                                    Pincode: {selectedPincode}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Action Buttons */}
                                                <div className="flex gap-3 pt-2">
                                                    <button
                                                        type="button"
                                                        onClick={cancelDeliveryEdit}
                                                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={saveDeliveryChanges}
                                                        disabled={!newDeliveryInfo || pincodeLoading}
                                                        className="flex-1 px-4 py-2 bg-[#dc2626] text-white rounded-lg hover:bg-[#dc2626]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                                    >
                                                        Save Changes
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Cart Items */}
                                <div className="rounded-xl shadow-sm p-6 bg-white">
                                    <div className="flex items-center gap-3 mb-4">
                                        <ShoppingBag className="w-6 h-6 text-[#dc2626]" />
                                        <h2 className="text-xl font-bold text-gray-900 font-['Crimson_Text']">
                                            Order Items
                                        </h2>
                                    </div>
                                    
                                    <div className="space-y-4">
                                        {cartItems.map((item, index) => (
                                            <div key={index} className="flex items-center gap-4 py-4 border-b last:border-0">
                                                <img
                                                    src={item.image}
                                                    alt={item.name}
                                                    className="w-16 h-16 sm:w-16 sm:h-16 object-cover rounded-lg"
                                                />
                                                <div className="ml-2 sm:ml-3 flex-1">
                                                    <h4 className="font-medium text-xs sm:text-sm text-gray-900">{item.name}</h4>
                                                    <OrderItemWeight item={item} />
                                                    <div className="mt-1">
                                                        <span className="text-xs text-[#dc2626] font-medium">
                                                            ₹{item.priceType === 'perDozen' ?
                                                                (item.dozenPrice || item.price).toFixed(2) :
                                                                item.priceType === 'perPack' ?
                                                                (item.packPrice || item.price).toFixed(2) :
                                                                item.price.toFixed(2)}
                                                            {item.priceType === 'perKg' && '/kg'}
                                                            {item.priceType === 'perDozen' && '/dozen'}
                                                            {item.priceType === 'perPack' && '/pack'}
                                                        </span>
                                                        <span className="text-xs text-gray-500 ml-2">
                                                            × {item.quantity}
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-[#dc2626] font-medium mt-1">
                                                        Subtotal: ₹{calculateItemPrice(item).toFixed(2)}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Right Column - Order Summary */}
                            <div className="lg:col-span-1">
                                <div className="rounded-xl shadow-sm p-6 sticky top-24 bg-white">
                                    <h2 className="text-xl font-bold mb-6 text-gray-900 font-['Crimson_Text']">
                                        Order Summary
                                    </h2>
                                    
                                    <div className="space-y-4">
                                        <div className="flex justify-between text-gray-600">
                                            <span>Items Subtotal:</span>
                                            <span className="font-medium">₹{itemsPrice.toFixed(2)}</span>
                                        </div>
                                        
                                        <div className="flex justify-between text-gray-600">
                                            <span>Delivery Charge:</span>
                                            <span className="font-medium">₹{shippingPrice.toFixed(2)}</span>
                                        </div>
                                        
                                        <div className="border-t pt-4">
                                            <div className="flex justify-between items-center">
                                                <span className="font-medium">Total Amount:</span>
                                                <div className="flex items-center text-xl font-bold text-[#dc2626]">
                                                    <IndianRupee className="w-5 h-5 mr-1" />
                                                    {totalPrice.toFixed(2)}
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Payment Methods */}
                                        <div className="space-y-4 border-t pt-4">
                                            <h3 className="font-semibold">Select Payment Method</h3>
                                            <div className="space-y-3">
                                                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                                                    <input
                                                        type="radio"
                                                        name="paymentMethod"
                                                        value="COD"
                                                        checked={paymentMethod === 'COD'}
                                                        onChange={(e) => setPaymentMethod(e.target.value)}
                                                        className="text-[#dc2626] focus:ring-[#dc2626]"
                                                    />
                                                    <div>
                                                        <p className="font-medium">Cash on Delivery</p>
                                                        <p className="text-sm text-gray-500">Pay when you receive</p>
                                                    </div>
                                                </label>
                                                
                                                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                                                    <input
                                                        type="radio"
                                                        name="paymentMethod"
                                                        value="Online"
                                                        checked={paymentMethod === 'Online'}
                                                        onChange={(e) => setPaymentMethod(e.target.value)}
                                                        className="text-[#dc2626] focus:ring-[#dc2626]"
                                                    />
                                                    <div>
                                                        <p className="font-medium">Online Payment</p>
                                                        <p className="text-sm text-gray-500">Cards, UPI, Netbanking</p>
                                                    </div>
                                                </label>
                                            </div>
                                        </div>
                                        
                                        <button
                                            onClick={processToPayment}
                                            disabled={loading || !paymentMethod || editingDelivery}
                                            className={`w-full py-3 rounded-lg font-medium flex items-center justify-center gap-2
                                                ${loading || !paymentMethod || editingDelivery
                                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                    : 'bg-[#dc2626] text-white hover:bg-[#dc2626]/90'
                                                } transition-colors`}
                                        >
                                            {loading ? (
                                                <>
                                                    <Loader2 className="w-5 h-5 mr-2 animate-spin"/>
                                                    Processing...
                                                </>
                                            ) : paymentMethod === 'COD' ? (
                                                'Place Order'
                                            ) : (
                                                'Proceed to Payment'
                                            )}
                                        </button>

                                        {editingDelivery && (
                                            <p className="text-xs text-amber-600 text-center">
                                                Please save delivery area changes before proceeding
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ConfirmOrder;